import { AlertProps, TypographyProps } from '@mui/material';

import { IconOption } from '@components/base';
import { Time } from '@enums/Time';

type TAvailabilityMap = Record<
  Time,
  {
    icon?: IconOption;
    severity?: AlertProps['severity'];
    textColor?: TypographyProps['color'];
  }
>;

export const AvailabilityMap: TAvailabilityMap = {
  [Time.FUTURE]: { icon: 'clock', severity: 'warning' },
  [Time.PAST]: { icon: 'clockWarning', severity: 'error', textColor: 'textDisabled' },
  [Time.NOW]: { severity: 'success', textColor: 'warning' },
  [Time.NEVER]: { icon: 'clockError', severity: 'error' },
  [Time.ALWAYS]: { severity: 'info' },
  [Time.INDEFINITELY]: { severity: 'success' },
};

const transformTimeStamp = (timestamp: number | string): number =>
  typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();

export const getAvailability = (
  initFrom?: null | number | string,
  initTo?: null | number | string,
  defaultAvailability: Time | undefined = Time.ALWAYS
) => {
  if (initFrom == null && initTo == null) return defaultAvailability;
  const from = initFrom != null ? initFrom : new Date(0).toDateString();
  const to = initTo != null ? initTo : new Date('2300/01/01').toDateString();

  const now = new Date().getTime();
  const fromTime = transformTimeStamp(from);
  const toTime = transformTimeStamp(to);

  if (initFrom && transformTimeStamp(initFrom) < now && !initTo) return Time.INDEFINITELY;
  if (fromTime < now && toTime > now) return Time.NOW;
  if (fromTime > now) return Time.FUTURE;
  if (toTime < now) return Time.PAST;
  return defaultAvailability;
};

export const isAvailable = (availability: Time) =>
  [Time.NOW, Time.ALWAYS, Time.INDEFINITELY].includes(availability);

type AvailabilityWindowItem = {
  availableFrom?: null | string;
  availableTo?: null | string;
};

const getBoundary = (
  items: readonly AvailabilityWindowItem[],
  field: keyof AvailabilityWindowItem,
  selectNext: (current: number, next: number) => boolean
) =>
  items.reduce<string | undefined>((selected, item) => {
    const value = item[field];
    if (!value || Number.isNaN(Date.parse(value))) return selected;
    if (!selected || selectNext(Date.parse(selected), Date.parse(value))) return value;
    return selected;
  }, undefined);

export const getAvailabilityWindow = (items: readonly AvailabilityWindowItem[]) => ({
  availableFrom: getBoundary(items, 'availableFrom', (current, next) => next < current),
  availableTo: getBoundary(items, 'availableTo', (current, next) => next > current),
});
